import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  saveOutputFileSettings,
  getOutputColumnsData,
} from "../store/workspaceSlice";
import MultiselectComponent from "../components/MultiselectComponent";

const initial = {
  rowsToSkip: 0,
  autoTruncate: false,
  truncatingColumn: "",
  removeDuplicateRows: false,
  columnsToAvoidInDuplicateCheck: [],
};

const Toggle = ({ checked, onChange }) => (
  <button
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${checked ? "bg-blue-600" : "bg-slate-300"}`}
  >
    <span
      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ${checked ? "translate-x-5" : "translate-x-0"}`}
    />
  </button>
);

const Stepper = ({ value, onChange, max = 999 }) => (
  <div className="flex items-center">
    <button
      onClick={() => onChange(Math.max(0, value - 1))}
      className="flex h-9 w-9 items-center justify-center rounded-l-lg border border-slate-200 bg-slate-50 text-lg font-semibold text-slate-500 hover:bg-slate-100 transition-colors"
    >
      −
    </button>
    <input
      type="number"
      value={value}
      onChange={(e) =>
        onChange(Math.max(0, Math.min(max, parseInt(e.target.value) || 0)))
      }
      className="h-9 w-16 border-y border-slate-200 bg-white text-center text-sm font-bold text-slate-800 outline-none font-mono"
    />
    <button
      onClick={() => onChange(Math.min(max, value + 1))}
      className="flex h-9 w-9 items-center justify-center rounded-r-lg border border-slate-200 bg-slate-50 text-lg font-semibold text-slate-500 hover:bg-slate-100 transition-colors"
    >
      +
    </button>
  </div>
);

const Hint = ({ text }) => (
  <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-medium text-blue-600">
    {text}
  </div>
);

const Card = ({ icon, label, children }) => (
  <div className=" rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div className="flex items-center gap-3 border-b border-slate-100 bg-slate-50 px-5 py-3.5">
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-base">
        {icon}
      </span>
      <span className="text-sm font-bold text-slate-800">{label}</span>
    </div>
    <div className="px-5 py-4">{children}</div>
  </div>
);

const OutputFileSettings = () => {
  const [s, setS] = useState(initial);
  const [isSaved, setIsSaved] = useState(false);
  const [toast, setToast] = useState(false);
  const outputColsData = useSelector(getOutputColumnsData);

  const update = (fields) => {
    setS((prev) => ({ ...prev, ...fields }));
    setIsSaved(false);
  };

  const handleSave = () => {
    setIsSaved(true);
    setToast(true);
    setTimeout(() => setToast(false), 2000);
    dispatch(
      saveOutputFileSettings({
        ...s,
      }),
    );
  };

  const handleReset = () => {
    setS(initial);
    setIsSaved(false);
    dispatch(
      saveOutputFileSettings({
        ...initial,
      }),
    );
  };

  const saveDisabled = s.autoTruncate && !s.truncatingColumn;
  const dispatch = useDispatch();
  const savedSettings = useSelector(
    (state) => state.workspace.outputFileSettings,
  );

  // load persisted settings when component mounts or store changes
  useEffect(() => {
    if (savedSettings && Object.keys(savedSettings).length) {
      setS((prev) => ({ ...prev, ...savedSettings }));
    }
  }, [savedSettings]);

  // generic save; passes the complete settings object to the store

  return (
    <div className="px-5 font-sans">
      <div className="mx-auto max-w-lg">
        {/* Header */}
        <div className="mb-2 text-center">
          <p className="mt-1 text-sm text-slate-500">
            Control how your exported file is processed.
          </p>
        </div>

        <div className="flex flex-col gap-3.5">
          {/* Rows to Skip */}
          <Card icon="↕️" label="Row Spacing">
            <p className="mb-3 text-xs text-slate-500">
              Insert empty rows between each data row in the output file.
            </p>
            <Stepper
              value={s.rowsToSkip}
              onChange={(v) => update({ rowsToSkip: v })}
            />
            {s.rowsToSkip > 0 && (
              <Hint
                text={`${s.rowsToSkip} empty row${s.rowsToSkip > 1 ? "s" : ""} will be inserted between each data row`}
              />
            )}
          </Card>

          {/* Auto Truncate */}
          <Card icon="✂️" label="Auto-Truncate">
            <p className="mb-3 text-xs text-slate-500">
              Select an output column — the feature finds the last non-empty
              cell in that column and removes all rows below it.
            </p>
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-700">
                Enable auto-truncate
              </span>
              <Toggle
                checked={s.autoTruncate}
                onChange={(v) => update({ autoTruncate: v })}
              />
            </div>
            <div
              className="overflow-hidden transition-all duration-300"
              style={{
                maxHeight: s.autoTruncate ? 140 : 0,
                marginTop: s.autoTruncate ? 14 : 0,
              }}
            >
              <label className="mb-2 block text-xs font-semibold text-slate-600">
                Select Output Column for Truncation
              </label>
              <select
                disabled={!s.autoTruncate}
                value={s.truncatingColumn}
                onChange={(e) => update({ truncatingColumn: e.target.value })}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
              >
                <option value={""}>Select column</option>

                {outputColsData.map((col) => (
                  <option key={col.outColChr} value={col.outColChr}>
                    {col.outColChr}
                  </option>
                ))}
              </select>
              {s.truncatingColumn && (
                <Hint
                  text={`Rows below the last non-empty cell in column ${s.truncatingColumn} will be removed`}
                />
              )}
            </div>
          </Card>

          {/* Remove Duplicates */}
          <Card icon="🧹" label="Remove Duplicate Rows">
            <p className="mb-3 text-xs text-slate-500">
              Strip identical rows, keeping only the first occurrence.
            </p>
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-700">
                Remove duplicate rows
              </span>
              <Toggle
                checked={s.removeDuplicateRows}
                onChange={(v) => update({ removeDuplicateRows: v })}
              />
            </div>
            {s.removeDuplicateRows && (
              <div className="mt-2">
                <MultiselectComponent
                  options={outputColsData.map((item) => ({
                    label: item.outColChr,
                    value: item.outColChr,
                  }))}
                  selectedOptions={s.columnsToAvoidInDuplicateCheck}
                  handleSetSelect={(selected) => {
                    update({ columnsToAvoidInDuplicateCheck: selected });
                  }}
                  label={"Columns to Ignore in Duplicate Check (Optional)"}
                  required={false}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                />
              </div>
            )}
            {s.removeDuplicateRows && (
              <Hint text="Duplicate rows will be stripped on export" />
            )}
          </Card>

          {/* Single Save / Reset bar */}
          <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
            <span
              className={`flex items-center gap-2 text-xs font-semibold transition-colors duration-200 ${isSaved ? "text-green-700" : "text-slate-400"}`}
            >
              <span
                className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] transition-colors duration-200 ${isSaved ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-400"}`}
              >
                {isSaved ? "✓" : "○"}
              </span>
              {isSaved ? "All settings saved" : "Unsaved changes"}
            </span>
            <div className="flex gap-2">
              <button
                onClick={handleReset}
                className="rounded-lg border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-500 transition-colors hover:bg-slate-50 cursor-pointer"
              >
                Reset All
              </button>
              <button
                onClick={handleSave}
                disabled={saveDisabled}
                className={`rounded-lg px-5 py-2 text-xs font-bold transition-colors ${saveDisabled ? "cursor-not-allowed bg-slate-100 text-slate-400" : "cursor-pointer bg-blue-600 text-white hover:bg-blue-700"}`}
              >
                Save Settings
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Toast */}
      <div
        className={`pointer-events-none fixed bottom-6 right-6 flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white shadow-xl transition-all duration-300 ${toast ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}
      >
        <span className="text-green-400">✓</span> Settings saved
      </div>
    </div>
  );
};

export default OutputFileSettings;
